import styled, { css, keyframes } from 'styled-components';
import InputMask from 'react-input-mask';

// ─── Design tokens ──────────────────────────────────────────────────────────
// As cores em si vêm das variáveis CSS de src/index.css (tema dark/light,
// alternado via ThemeContext). Os nomes abaixo só apontam pra essas variáveis,
// pra manter os componentes deste arquivo sem precisar mudar.

const ACCENT        = 'var(--accent)';
const ACCENT_SOFT    = 'var(--accent-soft)';
const ACCENT_GLOW    = 'var(--accent-glow)';
const ACCENT_BORDER  = 'var(--accent-border)';
const ACCENT_GRAD    = 'var(--accent-grad)';
const RED   = 'var(--red)';
const GREEN = 'var(--green)';

export const HeaderContainer = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 16px;
  height: 56px;
  background: var(--header-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1000;
  box-sizing: border-box;
`;

export const LeftCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const LogoGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

export const LogoButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px 8px;
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: ${ACCENT_GLOW};
  }
`;

export const LogoImage = styled.img`
  width: 24px;
  height: 24px;
`;

export const LogoText = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.3px;
  white-space: nowrap;
`;

export const SubTextBadge = styled.span`
  font-size: 12px;
  font-weight: 400;
  padding: 4px 15px;
  border-radius: 20px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  background: ${ACCENT_GLOW};
  color: ${ACCENT_SOFT};
  border: 1px solid ${ACCENT_BORDER};
`;

export const ContextChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  font-size: 13px;
  color: var(--muted2);
  max-width: 280px;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const ContextText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ContextDot = styled.span`
  color: var(--muted);
  flex-shrink: 0;
`;

export const NavGroup = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const NavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--muted2);
  font-size: 13.5px;
  font-weight: 450;
  padding: 6px 10px;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
  white-space: nowrap;

  &:hover {
    color: ${ACCENT_SOFT};
    background: ${ACCENT_GLOW};
  }
`;

export const NavDivider = styled.div`
  width: 1px;
  height: 16px;
  background: var(--border-strong);
  margin: 0 4px;
  flex-shrink: 0;
`;

export const SignInButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${ACCENT_GLOW};
  border: 1.5px solid ${ACCENT_BORDER};
  cursor: pointer;
  color: ${ACCENT_SOFT};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 20px;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
  margin-left: 4px;

  &:hover {
    background: ${ACCENT_BORDER};
    border-color: ${ACCENT};
  }
`;

export const InviteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 37px;
  padding: 0 16px;
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid var(--ghost-border);
  background: var(--ghost-surface);
  color: var(--muted2);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  margin: 0 4px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${ACCENT_GLOW};
    border-color: ${ACCENT_BORDER};
    color: ${ACCENT_SOFT};
  }
`;

export const AvatarWrapper = styled.div`
  position: relative;
  margin-left: 4px;
`;

export const AvatarButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${ACCENT_GLOW};
  border: 1.5px solid ${({ $open }) => ($open ? ACCENT_SOFT : ACCENT_BORDER)};
  color: ${ACCENT_SOFT};
  font-weight: 700;
  font-size: 11px;
  cursor: pointer;
  transition: border-color 0.18s ease;
`;

export const OnlineDot = styled.span`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${GREEN};
  border: 1.5px solid var(--bg);
`;

export const AvatarMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 8px;
  border-radius: 12px;
  overflow: hidden;
  background: var(--panel);
  border: 1px solid var(--border-strong);
  min-width: 180px;
  z-index: 50;
  box-shadow: var(--shadow-strong);
`;

export const AvatarMenuHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
`;

export const AvatarMenuName = styled.div`
  color: var(--text);
  font-size: 13px;
  font-weight: 600;
`;

export const AvatarMenuEmail = styled.div`
  color: var(--muted);
  font-size: 11px;
  margin-top: 2px;
`;

export const AvatarMenuSignOut = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${RED};
  font-size: 13px;
  transition: background 0.15s;

  &:hover {
    background: var(--surface);
  }
`;

// ─── Controles de board (stats, timer, ações) — mesclados no header ───────────

export const StatsMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--muted2);
  flex-shrink: 0;

  b {
    color: var(--text);
    font-weight: 700;
  }
`;

export const OnlineGroup = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const Dim = styled.span`
  color: var(--muted);
  font-weight: 400;
`;

export const Sep = styled.span`
  color: var(--muted);
`;

export const PulseDot = styled.span`
  position: relative;
  display: inline-flex;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${GREEN};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${GREEN};
    opacity: 0.75;
    animation: header-pulse 1.6s ease-out infinite;
  }

  @keyframes header-pulse {
    0% { transform: scale(1); opacity: 0.75; }
    100% { transform: scale(2.4); opacity: 0; }
  }
`;

const timerAlarmPulse = keyframes`
  0%, 100% { background: var(--surface); box-shadow: none; }
  50% {
    background: color-mix(in srgb, var(--red) 25%, transparent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--red) 25%, transparent);
  }
`;

export const CenterCluster = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  @media (max-width: 900px) {
    display: none;
  }
`;

const timerLivePulse = keyframes`
  0%, 100% { opacity: 0.75; transform: scale(1); }
  50% { opacity: 0.25; transform: scale(1.6); }
`;

export const TimerBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 4px 6px 4px 11px;
  border-radius: 20px;
  flex-shrink: 0;
  background: ${({ $running }) => ($running ? 'color-mix(in srgb, var(--accent) 10%, transparent)' : 'var(--surface)')};
  border: 1px solid ${({ $running }) => ($running ? 'color-mix(in srgb, var(--accent) 40%, transparent)' : 'var(--border)')};
  box-shadow: ${({ $running }) => ($running ? `0 4px 20px ${ACCENT_GLOW}` : 'none')};
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  ${({ $alarm }) => $alarm && css`
    animation: ${timerAlarmPulse} 0.9s ease-in-out 4;
  `}
`;

export const TimerIconWrapper = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const TimerLiveDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${ACCENT_SOFT};
  animation: ${timerLivePulse} 1.4s ease-in-out infinite;
`;

export const TimerInputStyled = styled(InputMask)`
  width: 50px;
  background: none;
  border: none;
  outline: none;
  text-align: center;
  font-family: 'SF Mono', 'Roboto Mono', ui-monospace, monospace;
  font-size: 13.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
  color: ${({ $isInvalid }) => ($isInvalid ? RED : 'var(--text)')};

  &:disabled {
    color: var(--text);
    opacity: 0.95;
  }

  &::placeholder {
    color: var(--muted);
  }
`;

export const TimerBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid ${({ $running }) => ($running ? 'color-mix(in srgb, var(--red) 35%, transparent)' : 'transparent')};
  cursor: pointer;
  flex-shrink: 0;
  background: ${({ $running }) => ($running ? 'color-mix(in srgb, var(--red) 18%, transparent)' : ACCENT_GRAD)};
  color: ${({ $running }) => ($running ? RED : 'var(--on-accent)')};
  box-shadow: ${({ $running }) => ($running ? 'none' : `0 3px 12px ${ACCENT_GLOW}`)};
  transition: transform 0.12s ease, background 0.15s ease;

  &:hover {
    transform: scale(1.08);
  }

  &:active {
    transform: scale(0.96);
  }
`;

export const BoardActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 37px;
  height: 37px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? ACCENT_BORDER : 'var(--ghost-border)')};
  background: ${({ $active }) => ($active ? ACCENT_GLOW : 'var(--ghost-surface)')};
  color: ${({ $active }) => ($active ? ACCENT_SOFT : 'var(--muted2)')};
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: ${ACCENT_GLOW};
    border-color: ${ACCENT_BORDER};
    color: ${ACCENT_SOFT};
  }
`;
